'use client';

import { TERMINAL_WINDOW_NAME } from '@shared/all/domains/electron';

// The detached terminal window. Opened by name so a second call reuses it
// rather than showing one pty twice — two tmux clients force the smaller size
// on both. It announces itself on a BroadcastChannel because the handle
// `open()` returns dies with the page, and switches sessions over that channel
// because re-opening the name with a new URL would reload the window.

export const TERMINAL_WINDOW_PATH = '/terminal';

/** No postId opens on the empty state, with the rail listing what is running. */
export type TerminalTarget = {
  spaceSlug: string;
  postId?: string;
  title?: string;
  runnerId?: number;
  /** Set to ask for the new-session launcher on arrival. A fresh value each
   *  time, so a second request to a window already on this board still counts
   *  as a new target. */
  launch?: string;
};

type Message =
  | { kind: 'hello' }
  | { kind: 'ping' }
  | { kind: 'bye' }
  | { kind: 'show'; target: TerminalTarget };

const CHANNEL = 'factiii-terminal-window';

function channel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  bus ??= new BroadcastChannel(CHANNEL);
  return bus;
}
let bus: BroadcastChannel | null = null;

function terminalWindowUrl(target: TerminalTarget): string {
  const params = new URLSearchParams({ space: target.spaceSlug });
  if (target.postId) params.set('post', target.postId);
  if (target.title) params.set('title', target.title);
  if (target.runnerId != null) params.set('runner', String(target.runnerId));
  if (target.launch) params.set('new', target.launch);
  return `${TERMINAL_WINDOW_PATH}?${params.toString()}`;
}

const FEATURES = 'width=1200,height=820,menubar=no,toolbar=no';

/** False when a popup blocker ate it — which is why every path here has to run
 *  inside the click, and why the caller checks before closing anything. */
export function openTerminalWindow(target: TerminalTarget): boolean {
  const w = window.open(
    terminalWindowUrl(target),
    TERMINAL_WINDOW_NAME,
    FEATURES
  );
  w?.focus();
  return Boolean(w);
}

/**
 * Raise the window without navigating it. Opening the *name* with an empty URL
 * is the only call that does both; `focus()` needs a handle the board no longer
 * has, and the window cannot raise itself with no gesture behind it.
 *
 * False also covers a stale record: a crash gets no `bye`, and `open('')` then
 * hands back a blank window for the caller to navigate properly.
 */
function raise(): boolean {
  if (!isOpen) return false;
  const existing = window.open('', TERMINAL_WINDOW_NAME, FEATURES);
  if (!existing || existing.closed || isBlank(existing)) {
    setOpen(false);
    return false;
  }
  existing.focus();
  return true;
}

/** Bring it forward, leaving the session it is on alone. */
export function focusTerminalWindow(fallback: TerminalTarget): void {
  if (!raise()) openTerminalWindow(fallback);
}

/** Bring it forward *on* `target` — what stops a second view of one pty. */
export function showTerminalWindow(target: TerminalTarget): void {
  if (!raise()) {
    openTerminalWindow(target);
    return;
  }
  channel()?.postMessage({ kind: 'show', target } satisfies Message);
}

/** A window we made rather than found. Cross-origin would throw, and that is
 *  not worth losing the window over. */
function isBlank(w: Window): boolean {
  try {
    return w.location.href === 'about:blank';
  } catch {
    return false;
  }
}

// ── whether a window is out there ───────────────────────────────────────────

let isOpen = false;
const listeners = new Set<() => void>();

function setOpen(next: boolean): void {
  if (isOpen === next) return;
  isOpen = next;
  for (const fn of listeners) fn();
}

export function subscribeTerminalWindow(onChange: () => void): () => void {
  listeners.add(onChange);
  attach();
  return () => {
    listeners.delete(onChange);
  };
}

export function terminalWindowIsOpen(): boolean {
  return isOpen;
}

export function terminalWindowIsOpenOnServer(): boolean {
  return false;
}

let attached = false;
function attach(): void {
  if (attached) return;
  const bc = channel();
  if (!bc) return;
  attached = true;
  bc.addEventListener('message', (e: MessageEvent<Message>) => {
    if (e.data?.kind === 'hello') setOpen(true);
    if (e.data?.kind === 'bye') setOpen(false);
  });
  // Catches a window that was already up when this board loaded.
  bc.postMessage({ kind: 'ping' } satisfies Message);
}

// ── the detached window's own side ──────────────────────────────────────────

/** Announce this window and answer the board. `/terminal` only. */
export function serveTerminalWindow(
  onShow: (target: TerminalTarget) => void
): () => void {
  const bc = channel();
  if (!bc) return () => {};
  const say = (m: Message) => bc.postMessage(m);
  const onMessage = (e: MessageEvent<Message>) => {
    if (e.data?.kind === 'ping') say({ kind: 'hello' });
    if (e.data?.kind === 'show') onShow(e.data.target);
  };
  // `pagehide`, not `unload`: it survives the bfcache and Safari honours it.
  const onHide = () => say({ kind: 'bye' });
  bc.addEventListener('message', onMessage);
  window.addEventListener('pagehide', onHide);
  say({ kind: 'hello' });
  return () => {
    bc.removeEventListener('message', onMessage);
    window.removeEventListener('pagehide', onHide);
    say({ kind: 'bye' });
  };
}
